import React from "react";
import { Box, Typography, Button, Stack, Chip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { tokens } from "../../../theme";
import { RootState } from "../../../store/Index";
import { ChevronRight } from "@mui/icons-material";
import FlashOnIcon from "@mui/icons-material/FlashOn";

interface HeroSectionProps {
  onPartnerClick: () => void;
  onSampleClick: () => void;
}

const HeroSection: React.FC<HeroSectionProps> = ({
  onPartnerClick,
  onSampleClick,
}) => {
  const theme = useTheme();
  const themeState = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, themeState);

  const handlePartnerClick = () => {
    onPartnerClick();
  };

  const handleSampleClick = () => {
    onSampleClick();
  };

  return (
    <Box
      component="section"
      sx={{
        position: "relative",
        pt: { xs: 10, md: 12 },
        pb: { xs: 16, md: 20 },
        overflow: "hidden",
        backgroundColor: "rgba(255, 255, 255, 0.6)",
      }}
    >
      {/* Background Gradient Blobs */}
      {/* <Box
        sx={{
          position: "absolute",
          top: 0,
          left: "50%",
          transform: "translateX(-50%)",
          width: "100%",
          height: "100%",
          zIndex: -1,
          opacity: 0.1,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: "25%",
            width: "384px",
            height: "384px",
            bgcolor: colors.primary[400],
            borderRadius: "50%",
            filter: "blur(128px)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            right: "25%",
            width: "384px",
            height: "384px",
            bgcolor: colors.secondary[400],
            borderRadius: "50%",
            filter: "blur(128px)",
          }}
        />
      </Box> */}

      {/* Content */}
      <Box
        sx={{
          maxWidth: "1280px",
          mx: "auto",
          px: { xs: 2, sm: 3, lg: 4 },
          textAlign: "center",
        }}
      >
        {/* Badge */}
        <Chip
          icon={<FlashOnIcon sx={{ fontSize: 16 }} />}
          label="NEXT-GEN FUNERAL ASSURANCE TECH"
          sx={{
            mb: 4,
            bgcolor: `${colors.primary[400]}10`,
            border: `1px solid ${colors.primary[400]}30`,
            color: colors.primary[400],
            fontWeight: 700,
            fontSize: "0.75rem",
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            "& .MuiChip-icon": {
              color: colors.primary[400],
            },
          }}
        />

        {/* Main Heading */}
        <Typography
          variant="h1"
          sx={{
            fontFamily: theme.typography.fontFamily,
            fontSize: { xs: "2.5rem", md: "3.5rem" },
            fontWeight: 900,
            color: colors.primary[100],
            lineHeight: 1.2,
            mb: 3,
          }}
        >
          Dignity in Every Pixel. <br />
          <Box
            component="span"
            sx={{
              background: colors.secondary[400],
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            Scale in Every Stream.
          </Box>
        </Typography>

        {/* Subtitle */}
        <Typography
          variant="h6"
          sx={{
            fontFamily: theme.typography.fontFamily,
            maxWidth: "800px",
            mx: "auto",
            fontSize: { xs: "1rem", md: "1.25rem" },
            color: colors.primary[300],
            mb: 5,
            lineHeight: 1.6,
          }}
        >
          Muzukuru empowers assurance companies to provide seamless,
          high-definition funeral streaming and digital legacy tools that keep
          your brand at the heart of the service.
        </Typography>

        {/* CTA Buttons */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="center"
        >
          <Button
            variant="contained"
            size="large"
            onClick={handlePartnerClick}
            sx={{
              bgcolor: colors.primary[400],
              color: colors.primary[900],
              px: 4,
              py: 2,
              fontSize: "1.125rem",
              fontWeight: 700,
              borderRadius: 3,
              textTransform: "none",
              boxShadow: `0 10px 40px ${colors.primary[400]}40`,
              "&:hover": {
                bgcolor: colors.primary[500],
                transform: "scale(1.05)",
              },
              transition: "all 0.3s ease",
            }}
          >
            Partner with Muzukuru
          </Button>
          <Button
            variant="outlined"
            size="large"
            endIcon={<ChevronRight />}
            onClick={handleSampleClick}
            sx={{
              borderColor: colors.primary[300],
              color: colors.primary[100],
              px: 4,
              py: 2,
              fontSize: "1.125rem",
              fontWeight: 700,
              borderRadius: 3,
              textTransform: "none",
              "&:hover": {
                borderColor: colors.primary[400],
                bgcolor: `${colors.primary[400]}10`,
              },
            }}
          >
            View Sample Stream
          </Button>
        </Stack>
      </Box>
    </Box>
  );
};

export default HeroSection;
